import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { useLang } from "../contexts/LangContext";
import api from "../lib/api";
import { Sailboat, Award, Anchor, Compass } from "lucide-react";

export default function Voile() {
  const { t, lang, img, visible, sectionOrder } = useLang();
  const ord = (id) => sectionOrder("voile", id, ["school", "boats", "brevets", "seatrips"]);
  const [boats, setBoats] = useState([]);

  useEffect(() => {
    api.get("/boats").then((r) => setBoats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="voile-page" className="flex flex-col">
      <PageHeader eyebrow={t.voile.eyebrow} title={t.voile.title} lead={t.voile.lead}
        image={img("voile", "header", "https://images.unsplash.com/photo-1561728130-afd430af0493?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")} />

      {/* School */}
      {visible("voile", "school") && (
      <section style={{ order: ord("school") }} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={img("voile", "school", "https://images.unsplash.com/photo-1503634192480-e77a6436f075?w=1200")} alt="" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="md:col-span-7">
            <div className="label-eyebrow text-club-red mb-4">{t.voile.schoolTitle}</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy">{t.voile.schoolTitle}</h2>
            <div className="divider-gold mt-5" />
            <p className="text-slate-600 mt-6 leading-relaxed text-lg">{t.voile.schoolText}</p>
          </div>
        </div>
      </section>
      )}

      {/* Boats */}
      {visible("voile", "boats") && (
      <section style={{ order: ord("boats") }} className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="label-eyebrow text-club-red mb-4">{t.voile.boatsTitle}</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy">{t.voile.boatsTitle}</h2>
          <div className="divider-gold mt-5" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {boats.map((b) => (
              <div key={b.id} className="group bg-white border border-slate-200 hover:border-club-red transition-colors" data-testid={`voile-boat-${b.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={b.main_image || (b.images || [])[0] || ""} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-widest text-club-yellow-dark mb-2 font-semibold">{b.type}</div>
                  <div className="font-serif text-2xl text-navy">{b.name}</div>
                  <div className="text-sm text-slate-600 mt-2">{b[`description_${lang}`]}</div>
                  <div className="text-xs text-slate-500 mt-3">{b.length} · {b.capacity} pers.</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Brevets */}
      {visible("voile", "brevets") && (
      <section style={{ order: ord("brevets") }} className="py-20 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Award className="w-9 h-9 text-club-yellow mb-6" strokeWidth={1.25} />
          <div className="label-eyebrow text-club-yellow mb-4">{t.voile.brevetsTitle}</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-light">{t.voile.brevetsTitle}</h2>
          <div className="divider-gold mt-5" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {t.voile.brevets.map((b, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 hover:border-club-yellow transition-colors" data-testid={`brevet-${i}`}>
                <span className="font-serif text-3xl text-club-yellow shrink-0">0{i + 1}</span>
                <div className="pt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Sea trips */}
      {visible("voile", "seatrips") && (
      <section style={{ order: ord("seatrips") }} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Compass className="w-10 h-10 text-club-red mb-6" strokeWidth={1.25} />
            <div className="label-eyebrow text-club-red mb-4">{t.voile.seaTripsTitle}</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy">{t.voile.seaTripsTitle}</h2>
            <div className="divider-gold mt-5" />
            <p className="text-slate-600 mt-6 leading-relaxed text-lg">{t.voile.seaTripsText}</p>
          </div>
          <div className="md:col-span-5">
            <img src={img("voile", "seatrips", "https://images.unsplash.com/photo-1498623116890-37e912163d5d?w=1200")} alt="" className="w-full aspect-square object-cover" />
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
